import React, { useState, useEffect } from 'react';
import { Button, Card, Steps, Result, Spin, Alert, Typography, Space } from 'antd';
import { 
    LoadingOutlined, 
    CheckCircleOutlined, 
    ExclamationCircleOutlined,
    ArrowLeftOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { usePayment } from '../../contexts/PaymentContext';
import { useCart } from '../../contexts/CartContext';
import errorHandler from '../../utils/errorHandler';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const PaymentProcessor = ({ 
    bookingData, 
    onSuccess, 
    onError, 
    onCancel,
    selectedPaymentMethod 
}) => {
    const { processPayment, loading } = usePayment();
    const { clearCart } = useCart();
    
    const [currentStep, setCurrentStep] = useState(0);
    const [paymentStatus, setPaymentStatus] = useState('pending');
    const [paymentResult, setPaymentResult] = useState(null);
    const [error, setError] = useState(null);

    const steps = [
        {
            title: 'Processing Payment',
            description: 'Please wait while we process your payment',
            icon: <LoadingOutlined />
        },
        {
            title: 'Verifying Payment',
            description: 'Confirming payment with payment gateway',
            icon: <CheckCircleOutlined />
        },
        {
            title: 'Completing Order',
            description: 'Finalizing your booking details',
            icon: <CheckCircleOutlined />
        }
    ];

    useEffect(() => {
        if (bookingData && selectedPaymentMethod) {
            initiatePayment();
        }
    }, [bookingData, selectedPaymentMethod]);

    const initiatePayment = async () => {
        try {
            setCurrentStep(0);
            setPaymentStatus('processing');
            setError(null);

            // Step 1: Process Payment
            const result = await processPayment({
                ...bookingData,
                payment_method: selectedPaymentMethod
            });

            setCurrentStep(1);
            setPaymentResult(result);

            // Step 2: Verify Payment (if needed)
            if (result.requires_verification) {
                await verifyPayment(result);
            }

            // Step 3: Complete Order
            setCurrentStep(2);
            await completeOrder(result);

            setPaymentStatus('success');
            onSuccess?.(result);

        } catch (error) {
            setPaymentStatus('failed');
            setError(error.message);
            onError?.(error);
        }
    };

    const verifyPayment = async (paymentResult) => {
        // Simulate verification delay
        await new Promise(resolve => setTimeout(resolve, 2000));
        return paymentResult;
    };

    const completeOrder = async (paymentResult) => {
        try {
            // Clear cart after successful payment
            clearCart();
            
            // Simulate order completion
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            return paymentResult;
        } catch (error) {
            throw new Error('Failed to complete order');
        }
    };

    const handleRetry = () => {
        setError(null);
        setPaymentStatus('pending');
        setCurrentStep(0);
        initiatePayment();
    };

    const handleCancel = () => {
        onCancel?.();
    };

    const renderProcessingView = () => (
        <div className="text-center py-8">
            <Spin 
                indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} 
                size="large"
            />
            <Title level={4} className="mt-4">
                {steps[currentStep]?.title}
            </Title>
            <Paragraph type="secondary">
                {steps[currentStep]?.description}
            </Paragraph>
            
            <div className="mt-6">
                <Steps 
                    current={currentStep} 
                    size="small"
                    items={steps.map(step => ({
                        title: step.title,
                        description: step.description
                    }))}
                />
            </div>
        </div>
    );

    const renderSuccessView = () => (
        <Result
            status="success"
            title="Payment Successful!"
            subTitle={
                <div>
                    <Paragraph>
                        Your payment has been processed successfully. 
                        Your booking is now confirmed.
                    </Paragraph>
                    {paymentResult && (
                        <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded">
                            <Text strong>Payment Details:</Text>
                            <div className="mt-2 space-y-1">
                                <div className="flex justify-between">
                                    <Text>Transaction ID:</Text>
                                    <Text code>{paymentResult.transaction_id}</Text>
                                </div>
                                <div className="flex justify-between">
                                    <Text>Amount:</Text>
                                    <Text strong>₹{paymentResult.amount?.toLocaleString()}</Text>
                                </div>
                                <div className="flex justify-between">
                                    <Text>Payment Method:</Text>
                                    <Text>{paymentResult.payment_method}</Text>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            }
            extra={[
                <Button 
                    type="primary" 
                    key="continue"
                    onClick={() => onSuccess?.(paymentResult)}
                >
                    Continue
                </Button>
            ]}
        />
    );

    const renderErrorView = () => (
        <Result
            status="error"
            title="Payment Failed"
            subTitle={
                <div>
                    <Paragraph>
                        We encountered an issue while processing your payment. 
                        Please try again or choose a different payment method.
                    </Paragraph>
                    {error && (
                        <Alert
                            message="Error Details"
                            description={error}
                            type="error"
                            showIcon
                            className="mt-4"
                        />
                    )}
                </div>
            }
            extra={[
                <Button 
                    key="retry"
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleRetry}
                    loading={loading}
                >
                    Try Again
                </Button>,
                <Button 
                    key="cancel"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleCancel}
                >
                    Go Back
                </Button>
            ]}
        />
    );

    const renderPendingView = () => (
        <div className="text-center py-8">
            <ExclamationCircleOutlined style={{ fontSize: 48, color: '#faad14' }} />
            <Title level={4} className="mt-4">
                Ready to Process Payment
            </Title>
            <Paragraph type="secondary">
                Click the button below to start the payment process
            </Paragraph>
            <Space className="mt-4">
                <Button 
                    type="primary"
                    size="large"
                    onClick={initiatePayment}
                    loading={loading}
                >
                    Process Payment
                </Button>
                <Button 
                    size="large"
                    onClick={handleCancel}
                >
                    Cancel
                </Button>
            </Space>
        </div>
    );

    return (
        <Card className="payment-processor">
            {paymentStatus === 'pending' && renderPendingView()}
            {paymentStatus === 'processing' && renderProcessingView()}
            {paymentStatus === 'success' && renderSuccessView()}
            {paymentStatus === 'failed' && renderErrorView()}
        </Card>
    );
};

export default PaymentProcessor;
